import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Box, Card, Image, Stack, Typography } from '@packages/shared';
import { Link } from '@packages/modules/src/Link/Link';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { CategoryRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryRecommendationFragmentFragmentDoc';
import { getClickTrackingBrandsCategoriesURLExtension } from '../../helpers/getClickTrackingBrandsCategoriesURLExtension';
import type { PageTemplate, RdeType } from '../../types';

/* GraphQL */ `
  fragment CategoryRecommendationFragment on CategoryRecommendation {
    category {
      categoryId
      categoryLink
      name
      topsellerProduct {
        imageUrl
        productId
      }
    }
    tracking {
      token
      logic
    }
  }
`;

export type RecoCategoryCardProps = {
  /**
   * number of brands
   */
  categoriesLength: number;
  /**
   * pageTemplate for products tracking
   */
  pageTemplate: PageTemplate;
  /**
   * position of product
   */
  position: number;
  recommendationData: FragmentType<typeof CategoryRecommendationFragmentFragmentDoc>;
  /**
   * Recommendation type
   */
  rdeType: RdeType;
};

export const RecoCategoryCard = ({
  categoriesLength,
  recommendationData,
  pageTemplate,
  position,
  rdeType,
}: RecoCategoryCardProps) => {
  const dispatchGTMEvent = useTracking();
  const { category, tracking } = getFragmentData(
    CategoryRecommendationFragmentFragmentDoc,
    recommendationData,
  );

  return (
    <Card sx={{ width: '100%', height: '100%' }}>
      <Link
        href={`${category.categoryLink}${
          category.categoryLink.endsWith('/') ? '' : '/'
        }?highlight=${
          category.topsellerProduct.productId
        }${getClickTrackingBrandsCategoriesURLExtension({
          cid: category.categoryId,
          itemsToLoad: categoriesLength,
          pageTemplate,
          recoLogic: tracking.logic,
          rdeType,
          name: category.name,
          position: position + 1,
        })}`}
        className="hover:text-color-primary no-underline hover:no-underline"
        onClick={() => {
          dispatchGTMEvent({
            event: 'ga_event',
            eventAction: 'click',
            eventCategory: 'recoClick',
            eventLabel: 'categoryRecoItemClick',
            eventValue: {
              trackingToken: tracking.token,
              pid: category.topsellerProduct.productId,
            },
          });
        }}
      >
        <Stack justifyContent="center">
          <Box sx={{ position: 'relative', px: 0.5, pt: 0.5 }}>
            <Image
              src={`${category.topsellerProduct.imageUrl}?$PL_square_hq$`}
              width={300}
              height={300}
              // TODO: add alt text to data structure
              alt={`Image of category: ${category.name}`}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              minHeight: 36,
            }}
          >
            <Typography
              title={category.name}
              component="p"
              variant="body2"
              sx={{
                px: 1.5,
                textAlign: 'center',
                overflow: 'hidden',
                lineHeight: 1,
              }}
            >
              {category.name}
            </Typography>
          </Box>
        </Stack>
      </Link>
    </Card>
  );
};
